// The masked map in tiles (issue 1144, slice 3; docs/VTT/design/30-sight-walls-and-light.md §5.3).
//
// A player's map picture, while the scene's vision or the manual fog is on, shows only the squares they may see of it:
// their sight groups' explored squares and, with the manual fog on, the DM's reveals (`mapShown`). Every other square
// is black, painted on the SERVER. The picture is served in tiles of TILE_SQUARES × TILE_SQUARES squares, cut along the
// fitted grid's lines (`axisPositions`), so a tile's edges are squares' edges even on an uneven grid.
//
// Each tile a player may see something of carries a HASH of exactly what they may see of it: the tile's grid lines and
// its shown squares. Nothing global, no counter: the same view of a tile is the same hash for everyone who has it,
// whatever their group, and another group exploring elsewhere changes none of it. A tile with nothing shown has no
// hash at all — the player's projection does not name it, and the server answers it as a missing picture.
import { axisPositions } from '../grid/cells.js';
import { normaliseSpans } from '../state/fog.js';
import { fogFloor, floored, heldFloors } from '../state/floorFog.js';
import { sceneFloors } from '../state/floors.js';
import { playerSight, sightExtent } from './sight.js';
/** A tile is this many squares a side (§5.3, decided). */
export const TILE_SQUARES = 10;
export function tileLayout(grid, extent) {
    const xs = axisPositions(grid, extent, 'x');
    const ys = axisPositions(grid, extent, 'y');
    const cols = Math.max(1, xs.length - 1);
    const rows = Math.max(1, ys.length - 1);
    return { xs, ys, cols, rows, across: Math.ceil(cols / TILE_SQUARES), down: Math.ceil(rows / TILE_SQUARES) };
}
export function tileBox(layout, ti, tj) {
    if (!Number.isInteger(ti) || !Number.isInteger(tj) || ti < 0 || tj < 0 || ti >= layout.across || tj >= layout.down)
        return null;
    const i0 = ti * TILE_SQUARES;
    const j0 = tj * TILE_SQUARES;
    const i1 = Math.min(layout.cols, i0 + TILE_SQUARES) - 1;
    const j1 = Math.min(layout.rows, j0 + TILE_SQUARES) - 1;
    return { i0, i1, j0, j1, x0: layout.xs[i0] ?? 0, x1: layout.xs[i1 + 1] ?? 0, y0: layout.ys[j0] ?? 0, y1: layout.ys[j1 + 1] ?? 0 };
}
export const tileKey = (ti, tj) => `${ti},${tj}`;
/** cyrb53: a small, fast, well-mixed 53-bit string hash. Not a secret — the server never trusts it — only an address. */
export function viewHash(text) {
    let h1 = 0xdeadbeef;
    let h2 = 0x41c6ce57;
    for (let k = 0; k < text.length; k += 1) {
        const c = text.charCodeAt(k);
        h1 = Math.imul(h1 ^ c, 2654435761);
        h2 = Math.imul(h2 ^ c, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}
/**
 * Every tile with at least one shown square, by `tileKey`. The hash is of the tile's own grid lines (rounded to the
 * pixel, as the server cuts) and its shown squares, so it moves exactly when what the tile's bytes show moves.
 */
export function tileViews(layout, shown) {
    const per = new Map();
    for (const s of shown) {
        if (s.j < 0 || s.j >= layout.rows)
            continue;
        const tj = Math.floor(s.j / TILE_SQUARES);
        const last = Math.min(s.i1, layout.cols - 1);
        for (let i = Math.max(0, s.i0); i <= last;) {
            const ti = Math.floor(i / TILE_SQUARES);
            const end = Math.min(last, (ti + 1) * TILE_SQUARES - 1);
            const key = tileKey(ti, tj);
            const list = per.get(key) ?? [];
            list.push({ j: s.j, i0: i, i1: end });
            per.set(key, list);
            i = end + 1;
        }
    }
    const out = new Map();
    for (const [key, list] of per) {
        const [ti, tj] = key.split(',').map(Number);
        const box = tileBox(layout, ti, tj);
        if (!box)
            continue;
        const spans = normaliseSpans(list);
        const count = spans.reduce((n, s) => n + s.i1 - s.i0 + 1, 0);
        const whole = count === (box.i1 - box.i0 + 1) * (box.j1 - box.j0 + 1);
        const lines = `${layout.xs.slice(box.i0, box.i1 + 2).map(Math.round).join(',')}|${layout.ys.slice(box.j0, box.j1 + 2).map(Math.round).join(',')}`;
        const what = whole ? 'all' : spans.map((s) => `${s.j - box.j0}:${s.i0 - box.i0}-${s.i1 - box.i0}`).join(';');
        out.set(key, { ti, tj, box, spans, whole, hash: viewHash(`${lines}|${what}`) });
    }
    return out;
}
/**
 * The squares a player's map shows (§5.1), or null when it shows all of it (the scene's vision and manual fog both off):
 * with vision on, their groups' explored squares and the DM's reveals while the manual fog is on; with only the manual
 * fog on, its reveals.
 */
export function mapShown(state, viewerId, floor = null) {
    const fogOn = state.fog?.on === true;
    const sight = playerSight(state, viewerId);
    // A floor's own map (qa-runs#1160) shows that floor's explored squares; the scene's map (the ground floor's) its own.
    const explored = sight ? (floor === null ? sight.explored : sight.exploredOn?.[floor] ?? []) : [];
    if (sight)
        return fogOn ? normaliseSpans([...explored, ...state.fog.revealed]) : explored;
    return fogOn ? state.fog.revealed : null;
}
/**
 * Which floor's squares mask a map picture for a player (qa-runs#1160): a floor's own map, that floor (one they hold,
 * should two floors share a picture); the scene's map, or any other, the ground floor (null). Null with no floors.
 */
export function mapFloorFor(state, assetId, viewerId) {
    if (!floored(state))
        return null;
    const held = heldFloors(state, viewerId);
    for (const f of sceneFloors(state.scene)) {
        if (f.map?.id !== assetId)
            continue;
        const at = fogFloor(state, { floor: f.id });
        if (held.has(at))
            return at;
    }
    return null;
}
const memo = new WeakMap();
/** A player's view of every tile they may see something of on a floor's map (memoised per state, viewer and floor), or null: the whole map. */
export function playerTileViews(state, viewerId, floor = null) {
    let per = memo.get(state);
    if (!per) {
        per = new Map();
        memo.set(state, per);
    }
    const key = floor === null ? viewerId : `${viewerId}@${floor}`;
    if (per.has(key))
        return per.get(key);
    const shown = mapShown(state, viewerId, floor);
    const views = shown ? tileViews(tileLayout(state.grid, sightExtent(state)), shown) : null;
    per.set(key, views);
    return views;
}
/**
 * The tiles of each other floor's own map a player holds (qa-runs#1160), by floor id, while the scene's vision is on
 * and it has floors: each floor's map is masked by that floor's explored squares. Null otherwise, or with none held.
 */
export function playerFloorTiles(state, viewerId) {
    if (!floored(state) || !playerSight(state, viewerId))
        return null;
    const out = {};
    const held = heldFloors(state, viewerId);
    for (const f of sceneFloors(state.scene)) {
        const at = fogFloor(state, { floor: f.id });
        if (at === null || !f.map || !held.has(at))
            continue;
        out[at] = playerTiles(state, viewerId, at) ?? {};
    }
    return Object.keys(out).length ? out : null;
}
/** What a player's projection carries (§5.3): each tile they may see something of, by `tileKey`, and its hash. */
export function playerTiles(state, viewerId, floor = null) {
    const views = playerTileViews(state, viewerId, floor);
    if (!views)
        return null;
    const out = {};
    for (const [key, v] of [...views].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)))
        out[key] = v.hash;
    return out;
}
//# sourceMappingURL=tiles.js.map